// Sub-components for the redesigned UnenrollDialog — extracted out of
// the main file to keep its line count reviewable. These are PURE
// presentational components: no Firestore calls, no Redux, all data
// comes in via props and all interaction is dispatched back out.
//
// Anything that touches form state, the save handler, or the org/student
// hooks stays in UnenrollDialog.tsx; this file is "just JSX".

import type { StudentType } from '@wonderschool/common-base-types';
import { Trans, useTranslation } from 'react-i18next';
import {
  bestDepositCandidate,
  calcProration,
  DepositCandidate,
  fmtCents,
  fmtShortDate,
  nextChargeDate,
  planLabelFor,
} from './unenrollHelpers';

// Sentinel ids — must stay in sync with UnenrollDialog.tsx.
export const DEPOSIT_PICK_NONE = 'none';
export const DEPOSIT_PICK_MANUAL = 'manual';

export type ActivePlan = {
  id: string;
  name?: string;
  // InvoicePlan.total is in dollars (e.g. 2300 = $2,300).
  total?: number;
  // InvoicePlan.billingInterval (e.g. "Monthly").
  billingInterval?: string;
  dateEnd?: number | string | null;
  // The student's actual chosen last day, written alongside dateEnd so the UI
  // always shows the human-readable date even when dateEnd is deferred for
  // arrears billing (see updateInvoicePlansEndDateForStudent).
  unenrollmentDate?: number | null;
  // The next time this plan will trigger an invoice. Used by the
  // money preview to surface the real next-invoice date instead of
  // the default "next 1st of month" fallback. Both field names are
  // observed in the wild — `chargeDate` is the canonical field on
  // invoice plans; `dateDue` is the related billing-dates field.
  chargeDate?: number;
  dateDue?: number;
  status?: string;
  students?: Array<{ id?: string; displayName?: string; firstName?: string; lastName?: string }>;
};

export type ResolvedDeposit = {
  amountCents: number;
  refundable: boolean;
  source: string;
};

type ProrationResult = ReturnType<typeof calcProration>;

// ─────────────────────────────────────────────────────────────────────────
// Plans picker
// ─────────────────────────────────────────────────────────────────────────
export function PlansPicker({
  allPlans,
  studentsById,
  selectedPlanIds,
  setSelectedPlanIds,
  loading,
  error,
  lastDayDate,
  studentId,
  studentName,
}: {
  allPlans: ActivePlan[];
  studentsById: Record<string, Partial<StudentType>>;
  selectedPlanIds: Array<string | null>;
  setSelectedPlanIds: (ids: Array<string | null>) => void;
  loading: boolean;
  error: string | null;
  lastDayDate: Date | null;
  studentId?: string;
  studentName?: string;
}) {
  const { t } = useTranslation();
  const validCount = selectedPlanIds.filter(Boolean).length;
  const fmtLastDay = lastDayDate ? fmtShortDate(lastDayDate) : '—';

  const updateRow = (index: number, value: string) => {
    const next = [...selectedPlanIds];
    next[index] = value || null;
    setSelectedPlanIds(next);
  };
  const removeRow = (index: number) => {
    const next = selectedPlanIds.filter((_, i) => i !== index);
    setSelectedPlanIds(next.length === 0 ? [null] : next);
  };
  const addRow = () => setSelectedPlanIds([...selectedPlanIds, null]);

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{t('enrollments.unenrollment.plansPicker.title')}</h3>
        <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
          <Trans
            i18nKey="enrollments.unenrollment.plansPicker.countPill"
            values={{ validCount, planCount: allPlans.length }}
          />
        </span>
      </div>
      <p className="mb-2 text-xs text-slate-500">
        <Trans i18nKey="enrollments.unenrollment.plansPicker.description" components={[<b key="fields" />]} />
      </p>

      {loading && (
        <div className="text-sm text-slate-500" role="status">
          {t('enrollments.unenrollment.plansPicker.loading')}
        </div>
      )}
      {error && !loading && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {t('enrollments.unenrollment.plansPicker.error')}
        </div>
      )}
      {!loading && !error && allPlans.length === 0 && (
        <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          {t('enrollments.unenrollment.plansPicker.empty')}
        </div>
      )}

      {!loading && !error && allPlans.length > 0 && (
        <div className="space-y-2">
          {selectedPlanIds.map((planId, index) => {
            const pickedElsewhere = new Set(selectedPlanIds.filter((id, i) => i !== index && id) as string[]);
            const chosen = allPlans.find((p) => p.id === planId);
            return (
              <div key={index} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <select
                      data-testid={`plan-row-${index}`}
                      className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                      value={planId ?? ''}
                      onChange={(e) => updateRow(index, e.target.value)}
                    >
                      <option value="">{t('enrollments.unenrollment.plansPicker.selectPlaceholder')}</option>
                      {allPlans.map((p) => (
                        <option key={p.id} value={p.id} disabled={pickedElsewhere.has(p.id)}>
                          {planLabelFor(p, studentsById)}
                        </option>
                      ))}
                    </select>
                    {chosen && (
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                        <a
                          href={`/billing/plans/${chosen.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 hover:text-blue-900"
                        >
                          {t('enrollments.unenrollment.plansPicker.seePaymentLink')}
                        </a>
                        <span>
                          <Trans
                            i18nKey="enrollments.unenrollment.plansPicker.currentlyEnds"
                            values={{
                              endDate:
                                (chosen.unenrollmentDate ?? chosen.dateEnd)
                                  ? new Date(
                                      (chosen.unenrollmentDate ?? chosen.dateEnd) as number | string
                                    ).toLocaleDateString()
                                  : t('enrollments.unenrollment.plansPicker.noEndDate'),
                            }}
                          />
                        </span>
                        <span>
                          <Trans
                            i18nKey="enrollments.unenrollment.plansPicker.willStopOn"
                            values={{ lastDay: fmtLastDay }}
                            components={[<b key="lastDay" className="text-slate-900" />]}
                          />
                        </span>
                        {(chosen.students?.length ?? 0) > 1 && (
                          <span className="flex items-center gap-1 text-amber-700">
                            {'⚠ '}
                            {t('enrollments.unenrollment.plansPicker.multiStudentWarning', {
                              count: (chosen.students?.length ?? 1) - 1,
                              name: studentName ?? t('enrollments.unenrollment.plansPicker.thisStudent'),
                            })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedPlanIds.length > 1 && (
                    <button
                      type="button"
                      aria-label={t('enrollments.unenrollment.plansPicker.removePlan')}
                      onClick={() => removeRow(index)}
                      className="mt-2 text-slate-400 hover:text-red-600"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <button type="button" onClick={addRow} className="text-sm font-medium text-blue-700 hover:text-blue-900">
            {t('enrollments.unenrollment.plansPicker.addPayment')}
          </button>
          <p className="mt-3 text-xs text-slate-500">
            {validCount === 0
              ? t('enrollments.unenrollment.plansPicker.noSelectionNote')
              : t('enrollments.unenrollment.plansPicker.confirmNote', {
                  count: validCount,
                  plural: validCount === 1 ? '' : 's',
                  lastDay: fmtLastDay,
                })}
          </p>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Deposit picker + money preview
// ─────────────────────────────────────────────────────────────────────────
export function DepositSection({
  candidates,
  effectivePickId,
  onPickChange,
  manualAmount,
  setManualAmount,
  manualRefundable,
  setManualRefundable,
  resolvedDeposit,
  proration,
  monthlyTuitionCents,
  noticeDays,
  lastDayDate,
  selectedPlans,
}: {
  candidates: DepositCandidate[];
  effectivePickId: string;
  onPickChange: (id: string) => void;
  manualAmount: string;
  setManualAmount: (v: string) => void;
  manualRefundable: boolean;
  setManualRefundable: (v: boolean) => void;
  resolvedDeposit: ResolvedDeposit | null;
  proration: ProrationResult;
  monthlyTuitionCents: number;
  noticeDays: number;
  lastDayDate: Date | null;
  // The plans currently picked in the plans dropdowns. Used to read
  // each plan's `chargeDate` so the "Appears on next invoice" line
  // shows the real billing cycle date, not a 1st-of-month guess.
  selectedPlans?: Array<{ chargeDate?: number; dateDue?: number }>;
}) {
  const { t } = useTranslation();
  const chosen = candidates.find((c) => c.id === effectivePickId) || null;
  const best = bestDepositCandidate(candidates);
  const isManual = effectivePickId === DEPOSIT_PICK_MANUAL;
  const isNone = effectivePickId === DEPOSIT_PICK_NONE;

  let pillClass = 'bg-slate-100 text-slate-500';
  let pillText = t('enrollments.unenrollment.depositSection.noDepositOnFilePill');
  if (resolvedDeposit && resolvedDeposit.amountCents > 0) {
    if (resolvedDeposit.refundable) {
      pillClass = 'bg-emerald-100 text-emerald-700';
      pillText = t('enrollments.unenrollment.depositSection.refundablePill', {
        amount: fmtCents(resolvedDeposit.amountCents),
      });
    } else {
      pillClass = 'bg-slate-200 text-slate-700';
      pillText = t('enrollments.unenrollment.depositSection.nonRefundablePill', {
        amount: fmtCents(resolvedDeposit.amountCents),
      });
    }
  }

  let matchPillClass = 'bg-blue-50 text-blue-700';
  let matchPillText = t('enrollments.unenrollment.depositSection.autoPickedPill');
  if (isNone) {
    matchPillClass = 'bg-slate-100 text-slate-500';
    matchPillText = t('enrollments.unenrollment.depositSection.noDepositMatchPill');
  } else if (!chosen || (best && chosen.id !== best.id)) {
    matchPillClass = 'bg-amber-100 text-amber-800';
    matchPillText = t('enrollments.unenrollment.depositSection.customPickedPill');
  }

  let helpText = '';
  if (chosen) helpText = chosen.note;
  else if (isManual) helpText = t('enrollments.unenrollment.depositSection.manualHelp');
  else if (candidates.length === 0) helpText = t('enrollments.unenrollment.depositSection.noCandidatesHelp');
  else helpText = t('enrollments.unenrollment.depositSection.noDepositHelp');

  // The "10%" case from review: a deposit was found (structured field
  // or legacy fee). Most schools/students don't have one, so when there
  // is one we surface a short explainer above the picker so the
  // director can't accidentally end the enrollment without realizing
  // the money implication.
  const hasDepositOnFile = !!resolvedDeposit && resolvedDeposit.amountCents > 0 && !isNone && !isManual;

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{t('enrollments.unenrollment.depositSection.title')}</h3>
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${pillClass}`}>{pillText}</span>
      </div>

      {hasDepositOnFile && resolvedDeposit && (
        <DepositExplainer refundable={resolvedDeposit.refundable} amountCents={resolvedDeposit.amountCents} />
      )}

      <div className="mb-3 rounded-lg border border-slate-200 bg-white p-3">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {t('enrollments.unenrollment.depositSection.whichDeposit')}
          </span>
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${matchPillClass}`}>
            {matchPillText}
          </span>
        </div>
        <select
          data-testid="depositPicker"
          value={effectivePickId}
          onChange={(e) => onPickChange(e.target.value)}
          className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
        >
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
          <option value={DEPOSIT_PICK_NONE}>{t('enrollments.unenrollment.depositSection.noDepositOption')}</option>
          <option value={DEPOSIT_PICK_MANUAL}>{t('enrollments.unenrollment.depositSection.manualOption')}</option>
        </select>
        <p className="mt-2 text-xs text-slate-500">{helpText}</p>
        {chosen?.href && (
          <a
            href={chosen.href}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block text-xs text-blue-700 hover:text-blue-900"
          >
            {chosen.linkLabel || t('enrollments.unenrollment.depositSection.seeWhereSaved')} ↗
          </a>
        )}

        {isManual && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-600">{t('enrollments.unenrollment.depositSection.amountLabel')}</span>
              <div className="mt-1 flex">
                <span className="inline-flex items-center rounded-l border border-r-0 border-slate-300 bg-slate-50 px-2 text-sm text-slate-500">
                  $
                </span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  className="w-full rounded-r border border-slate-300 px-2 py-1.5 text-sm"
                  data-testid="manualAmount"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs text-slate-600">
                {t('enrollments.unenrollment.depositSection.refundableLabel')}
              </span>
              <select
                value={manualRefundable ? 'true' : 'false'}
                onChange={(e) => setManualRefundable(e.target.value === 'true')}
                className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
                data-testid="manualRefundable"
              >
                <option value="true">{t('enrollments.unenrollment.depositSection.refundableYes')}</option>
                <option value="false">{t('enrollments.unenrollment.depositSection.refundableNo')}</option>
              </select>
            </label>
          </div>
        )}
      </div>

      <MoneyPreview
        proration={proration}
        monthlyTuitionCents={monthlyTuitionCents}
        noticeDays={noticeDays}
        resolvedDeposit={resolvedDeposit}
        lastDayDate={lastDayDate}
        selectedPlans={selectedPlans}
      />
    </section>
  );
}

// Blue info card surfaced ABOVE the picker when a deposit IS on file.
// 90%+ of students don't have one, so a provider clicking through
// shouldn't have to know to look for it; when one IS present we
// explain what the system is going to do.
function DepositExplainer({ refundable, amountCents }: { refundable: boolean; amountCents: number }) {
  const { t } = useTranslation();
  return (
    <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
      <div className="flex items-start gap-2">
        <svg
          className="mt-0.5 flex-shrink-0 text-blue-600"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 16v-4m0-4h.01M12 22a10 10 0 110-20 10 10 0 010 20z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <div className="space-y-1">
          <div className="font-semibold">
            <Trans
              i18nKey="enrollments.unenrollment.depositExplainer.hasDeposit"
              values={{ amount: fmtCents(amountCents) }}
            />
          </div>
          {refundable ? (
            <div className="text-blue-800">{t('enrollments.unenrollment.depositExplainer.manualRefundNote')}</div>
          ) : (
            <>
              <div className="text-blue-800">{t('enrollments.unenrollment.depositExplainer.keepNote')}</div>
              <div className="text-blue-800">{t('enrollments.unenrollment.depositExplainer.feeOnTop')}</div>
              <div className="text-blue-800">
                <Trans
                  i18nKey="enrollments.unenrollment.depositExplainer.handBookCover"
                  components={[<i key="covers" />, <b key="option" />]}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MoneyPreview({
  proration,
  monthlyTuitionCents,
  noticeDays,
  resolvedDeposit,
  lastDayDate,
  selectedPlans,
}: {
  proration: ProrationResult;
  monthlyTuitionCents: number;
  noticeDays: number;
  resolvedDeposit: ResolvedDeposit | null;
  lastDayDate: Date | null;
  selectedPlans?: Array<{ chargeDate?: number; dateDue?: number }>;
}) {
  const { t } = useTranslation();

  if (!lastDayDate) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
        {t('enrollments.unenrollment.moneyPreview.noDatePrompt')}
      </div>
    );
  }

  const noTuitionWarning = monthlyTuitionCents === 0 && (
    <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
      <span aria-hidden="true">⚠ </span>
      {t('enrollments.unenrollment.noTuitionOnRecord')}
    </div>
  );

  const meetsNoticePill = proration.meetsNotice ? (
    <span className="ml-1 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
      {t('enrollments.unenrollment.moneyPreview.enoughWarning')}
    </span>
  ) : (
    <span className="ml-1 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
      {t('enrollments.unenrollment.moneyPreview.notEnoughWarning')}
    </span>
  );

  // 90% of students have no deposit — so when one isn't selected, the
  // money preview is a simpler three-row block (tuition → notice →
  // prorate → net) without any deposit rows at all.
  const showDepositRow = !!resolvedDeposit && resolvedDeposit.amountCents > 0;

  let netLabel = t('enrollments.unenrollment.moneyPreview.netZero');
  let netClass = 'text-slate-900';
  let netVal = '$0.00';
  if (proration.willCharge) {
    netLabel = t('enrollments.unenrollment.moneyPreview.netCharge');
    netClass = 'text-red-700';
    netVal = fmtCents(proration.netOwedCents);
  } else if (proration.willRefund) {
    netLabel = t('enrollments.unenrollment.moneyPreview.netRefund');
    netClass = 'text-emerald-700';
    netVal = fmtCents(-proration.netOwedCents);
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
      {noTuitionWarning}
      <div className="flex justify-between">
        <span className="text-slate-600">{t('enrollments.unenrollment.moneyPreview.monthlyTuition')}</span>
        <span className="font-medium">
          <Trans
            i18nKey="enrollments.unenrollment.moneyPreview.amountPerMonth"
            values={{ amount: fmtCents(monthlyTuitionCents) }}
          />
        </span>
      </div>
      <div className="mt-1 flex justify-between">
        <span className="text-slate-600">{t('enrollments.unenrollment.moneyPreview.daysGiven')}</span>
        <span className="font-medium">
          <Trans
            i18nKey="enrollments.unenrollment.moneyPreview.noticeDaysProgress"
            values={{ daysNotice: proration.daysNotice, noticeDays }}
          />{' '}
          {meetsNoticePill}
        </span>
      </div>
      {proration.prorateDays > 0 && (
        <div className="mt-1 flex justify-between">
          <span className="text-slate-600">
            {t('enrollments.unenrollment.moneyPreview.shortNoticeFeeLabel')}
            <span className="text-xs text-slate-500">
              {' '}
              <Trans
                i18nKey="enrollments.unenrollment.moneyPreview.shortNoticeFeeCalc"
                values={{
                  prorateDays: proration.prorateDays,
                  plural: proration.prorateDays === 1 ? '' : 's',
                  dailyRate: fmtCents(proration.dailyCents),
                }}
              />
            </span>
          </span>
          <span className="font-medium">{fmtCents(proration.prorateAmountCents)}</span>
        </div>
      )}

      {/* Deposit rows — only when the picker resolved to a real deposit
          on file. In the no-deposit (90%) case these stay hidden and the
          preview reads as a clean short-notice-fee calculation. */}
      {showDepositRow && resolvedDeposit?.refundable && (
        <>
          <div className="mt-1 flex justify-between">
            <span className="text-slate-600">{t('enrollments.unenrollment.moneyPreview.depositHeld')}</span>
            <span className="font-medium">{fmtCents(resolvedDeposit.amountCents)}</span>
          </div>
          {proration.prorateAmountCents > 0 && (
            <div className="mt-1 flex justify-between">
              <span className="text-slate-600">{t('enrollments.unenrollment.moneyPreview.depositCredit')}</span>
              <span className="font-medium text-emerald-700">– {fmtCents(proration.depositCreditCents)}</span>
            </div>
          )}
        </>
      )}
      {showDepositRow && resolvedDeposit && !resolvedDeposit.refundable && (
        <div className="mt-1 flex justify-between">
          <span className="text-slate-600">{t('enrollments.unenrollment.moneyPreview.depositHeld')}</span>
          <span className="font-medium">
            {fmtCents(resolvedDeposit.amountCents)}{' '}
            <span className="text-xs text-slate-500">{t('enrollments.unenrollment.moneyPreview.schoolKeepsNote')}</span>
          </span>
        </div>
      )}
      <div className="my-2 border-t border-slate-200" />
      <div className="flex justify-between">
        <span className={`font-semibold ${netClass}`}>{netLabel}</span>
        <span className={`font-bold ${netClass}`}>{netVal}</span>
      </div>

      {lastDayDate && proration.willCharge && (
        <ExpectedPaymentDates lastDayDate={lastDayDate} selectedPlans={selectedPlans} />
      )}
    </div>
  );
}

function ExpectedPaymentDates({
  lastDayDate,
  selectedPlans,
}: {
  lastDayDate: Date;
  selectedPlans?: Array<{ chargeDate?: number; dateDue?: number }>;
}) {
  const { t } = useTranslation();
  // Read the next charge date off the picked plans' chargeDate fields
  // when available — falls back to next-1st-of-month when no plan
  // exposes one. Avoids promising "Sep 1" for a family on the 15th.
  const chargeAppears = nextChargeDate(lastDayDate, selectedPlans || []);
  return (
    <div className="mt-2 space-y-1 border-t border-slate-100 pt-2 text-xs">
      <div className="flex justify-between">
        <span className="text-slate-500">{t('enrollments.unenrollment.paymentDates.chargeShows')}</span>
        <span className="font-medium text-slate-700">
          {fmtShortDate(chargeAppears)}{' '}
          <span className="font-normal text-slate-400">
            {t('enrollments.unenrollment.paymentDates.billingCycleNote')}
          </span>
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-slate-500">{t('enrollments.unenrollment.paymentDates.cardCharged')}</span>
        <span className="font-medium text-slate-700">
          {fmtShortDate(chargeAppears)}{' '}
          <span className="font-normal text-slate-400">{t('enrollments.unenrollment.paymentDates.billPaidNote')}</span>
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Submit-summary bullets
// ─────────────────────────────────────────────────────────────────────────
export function SubmitSummary({
  lastDayDate,
  planCount,
  resolvedDeposit,
  proration,
  selectedPlans,
  childFirstName,
}: {
  lastDayDate: Date | null;
  planCount: number;
  resolvedDeposit: ResolvedDeposit | null;
  proration: ProrationResult;
  selectedPlans?: Array<{ chargeDate?: number; dateDue?: number }>;
  childFirstName?: string;
}) {
  const { t } = useTranslation();
  const bullets: string[] = [];
  if (lastDayDate)
    bullets.push(
      t('enrollments.unenrollment.lastDayAtSchool.label', {
        childName: childFirstName || t('enrollments.unenrollment.submitSummary.theChild'),
        lastDay: fmtShortDate(lastDayDate),
      })
    );
  if (planCount > 0)
    bullets.push(
      t('enrollments.unenrollment.submitSummary.plansStop', {
        count: planCount,
        plural: planCount === 1 ? '' : 's',
      })
    );
  if (lastDayDate && resolvedDeposit?.refundable && proration.willRefund) {
    bullets.push(
      t('enrollments.unenrollment.submitSummary.manualRefund', { amount: fmtCents(-proration.netOwedCents) })
    );
  } else if (lastDayDate && proration.willCharge) {
    bullets.push(
      t('enrollments.unenrollment.submitSummary.charge', {
        amount: fmtCents(proration.netOwedCents),
        chargeDate: fmtShortDate(nextChargeDate(lastDayDate, selectedPlans || [])),
      })
    );
  }
  if (bullets.length === 0) return null;
  return (
    <section className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
      <p className="font-semibold">{t('enrollments.unenrollment.submitSummary.buttonPrompt')}</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// What to tell the family — copyable script the provider can read aloud
// or paste into a goodbye email. Surfaces the same facts as the submit
// summary but phrased as a conversation with the parent.
// ─────────────────────────────────────────────────────────────────────────
export function FamilyScript({
  lastDayDate,
  resolvedDeposit,
  proration,
  selectedPlans,
  childFirstName,
}: {
  lastDayDate: Date | null;
  resolvedDeposit: ResolvedDeposit | null;
  proration: ProrationResult;
  selectedPlans?: Array<{ chargeDate?: number; dateDue?: number }>;
  childFirstName?: string;
}) {
  const { t } = useTranslation();
  if (!lastDayDate) return null;
  const childName = childFirstName || t('enrollments.unenrollment.familyScript.yourChild');
  const last = fmtShortDate(lastDayDate);
  const lines: string[] = [];
  lines.push(
    t('enrollments.unenrollment.lastDayAtOurSchool.label', {
      childName,
      lastDay: last,
    })
  );
  if (proration.willRefund && resolvedDeposit?.refundable) {
    lines.push(
      t('enrollments.unenrollment.familyScript.manualRefund', {
        amount: fmtCents(-proration.netOwedCents),
      })
    );
  } else if (proration.willCharge) {
    const charge = nextChargeDate(lastDayDate, selectedPlans || []);
    lines.push(
      t('enrollments.unenrollment.familyScript.shortNoticeFee', {
        chargeDate: fmtShortDate(charge),
        amount: fmtCents(proration.netOwedCents),
      })
    );
  } else if (proration.willCharge === false && proration.willRefund === false) {
    lines.push(t('enrollments.unenrollment.familyScript.paidInFull'));
  }
  return (
    <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
      <p className="font-semibold">{t('enrollments.unenrollment.familyScript.title')}</p>
      <div className="mt-1 space-y-1 italic">
        {lines.map((l, i) => (
          <p key={i}>"{l}"</p>
        ))}
      </div>
      <p className="mt-2 text-xs text-emerald-700">{t('enrollments.unenrollment.familyScript.copyHint')}</p>
    </section>
  );
}
